import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MaterialModule } from '@app/material.module';
import { ExternalInvoice } from '@app/types/external-invoice.type';

const RFC_PATTERN = /^[A-Z&Ña-z&ñ]{3,4}\d{6}[A-Z0-9a-z]{3}$/;

interface EditDialogData {
    invoice: ExternalInvoice;
    documentType: 'INVOICE' | 'REFERRAL' | 'PRE_INVOICE';
}

/**
 * Convierte los valores centinela de IA a string vacío para poder editarlos.
 */
function editableValue(value: any): string {
    if (value === null || value === undefined) return '';
    const str = String(value).trim();
    if (!str || str.toUpperCase() === 'NO ENCONTRADO' || str === 'REVISION_REQUERIDA') return '';
    return str;
}

function toDateInputValue(value: any): string {
    if (!value) return '';
    const d = value instanceof Date ? value : new Date(value);
    if (isNaN(d.getTime())) return '';
    return d.toISOString().slice(0, 10);
}

@Component({
    selector: 'app-edit-external-invoice-dialog',
    standalone: true,
    imports: [CommonModule, MaterialModule, ReactiveFormsModule],
    templateUrl: './edit-external-invoice-dialog.component.html',
    styleUrl: './edit-external-invoice-dialog.component.scss'
})
export class EditExternalInvoiceDialogComponent {
    private fb = inject(FormBuilder);
    private dialogRef = inject(MatDialogRef<EditExternalInvoiceDialogComponent>);
    public data: EditDialogData = inject(MAT_DIALOG_DATA);

    isReferral = false;

    editForm: FormGroup;

    constructor() {
        const inv = this.data.invoice;
        this.isReferral = this.data.documentType === 'REFERRAL';

        this.editForm = this.fb.group({
            uuid: [editableValue(inv.uuid)],
            serie: [editableValue(inv.serie)],
            folio: [editableValue(inv.folio)],
            emisorNombre: [editableValue(inv.emisorNombre)],
            emisorRFC: [editableValue(inv.emisorRFC), [Validators.pattern(RFC_PATTERN)]],
            receptorNombre: [editableValue(inv.receptorNombre)],
            receptorRFC: [editableValue(inv.receptorRFC), [Validators.pattern(RFC_PATTERN)]],
            fechaEmision: [toDateInputValue(inv.fechaEmision ?? inv.fechaRemision)],
            total: [editableValue(inv.total)],
            moneda: [editableValue(inv.moneda)],
            folioRemision: [editableValue(inv.folioRemision)],
            folioOrden: [editableValue(inv.folioOrden)],
            proveedorNombre: [editableValue(inv.proveedorNombre)],
            proveedorRFC: [editableValue(inv.proveedorRFC), [Validators.pattern(RFC_PATTERN)]],
        });
    }

    get invoiceTitle(): string {
        const inv = this.data.invoice;
        if (this.isReferral) return inv.folioRemision || inv.documentFolio || inv.id.slice(0, 8);
        return inv.uuid || inv.folio || inv.id.slice(0, 8);
    }

    onCancel(): void {
        this.dialogRef.close();
    }

    onSave(): void {
        if (this.editForm.invalid) return;

        const raw = this.editForm.getRawValue();
        const changes: Record<string, any> = {};

        for (const [key, value] of Object.entries(raw)) {
            const strValue = String(value ?? '').trim();
            // Vacío => null (limpia valores centinela como NO ENCONTRADO)
            changes[key] = strValue === '' ? null : strValue;
        }

        // Convertir fechas y números a su tipo
        if (changes['fechaEmision']) changes['fechaEmision'] = new Date(changes['fechaEmision']).toISOString();
        if (changes['total'] !== null) {
            const num = parseFloat(String(changes['total']).replace(/[^0-9.-]+/g, ''));
            changes['total'] = isNaN(num) ? null : num;
        }
        if (changes['emisorRFC']) changes['emisorRFC'] = changes['emisorRFC'].toUpperCase();
        if (changes['receptorRFC']) changes['receptorRFC'] = changes['receptorRFC'].toUpperCase();
        if (changes['proveedorRFC']) changes['proveedorRFC'] = changes['proveedorRFC'].toUpperCase();

        // El backend solo agrega statusHistory si 'estatus' viene en changes
        changes['estatus'] = this.data.invoice.estatus;
        changes['statusHistory'] = {
            estatus: this.data.invoice.estatus,
            fecha: new Date().toISOString(),
            observacion: 'Corrección manual de datos',
            userId: localStorage.getItem('userId') || 'unknown'
        };

        this.dialogRef.close({ id: this.data.invoice.id, changes });
    }
}
